import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Heading, Panel } from '@savgroup-front-common/core';
import { Modal, MODAL_SIZES } from '@savgroup-front-common/core/src/atoms/modal';
import { Switch, Autocomplete, Input } from '@savgroup-front-common/core/src/atoms/Form';
import { Tag } from '@savgroup-front-common/core/src/atoms/Tag';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';
import { CrossIcon } from '@savgroup-front-common/core/src/protons/icons/Cross';
import { QrCodeIcon } from '@savgroup-front-common/core/src/protons/icons/QrCode.icon';

import { InventoryMobileQrModal } from '../components/InventoryMobileQr';
import { ThumbZoneOverlay } from '../components/ThumbZoneOverlay';
import { useInventoryThumbZone } from '../context/InventoryThumbZoneContext';
import {
  type InventoryViewMode,
  useInventoryViewMode,
} from '../context/InventoryViewModeContext';
import { getBoxMartinNeuillyInventoryHeader } from '../data/inventoryBoxMartinDataset';
import { computeInventoryAmountEcartsFromLines } from '../utils/inventoryAmountEcarts';
import { useCurrentUserEmail } from '../hooks/useCurrentUserEmail';
import {
  loadMockLineValidationsForInventory,
  persistMockLineComptageValide,
} from '../utils/inventoryLineValidationStorage';
import {
  appendManualLineToInventory,
  isManualInventoryLineId,
  loadManualLinesForInventory,
  updateManualLineFields,
} from '../utils/inventoryManualPieceStorage';
import {
  loadSupervisorControlsForInventory,
  persistSupervisorControlValide,
} from '../utils/inventorySupervisorControlStorage';
import {
  buildInventoryMobileOpenUrl,
  parseInventoryViewModeFromMobileQrSearch,
} from '../utils/inventoryMobileQr.utils';
import {
  isInventoryClosed,
  persistInventoryClosure,
  POC_INVENTORY_CLOSURE_EVENT,
} from '../utils/inventoryClosureStorage';
import { applyInventoryStockImputation } from '../utils/inventoryStockImputation';
import { POC_INVENTORY_NOM_EVENT, resolveInventoryNom } from '../utils/inventoryNomStorage';
import {
  getSparePartCatalogEntry,
  SPARE_PARTS_CATALOG_OPTIONS,
} from '../constants/sparePartsCatalog';

type InventoristeResponsiveEntryMode = 'manuel' | 'scan';

/** Largeur max. (px) pour la vue « responsive » inventaire (résumé, switch Manuel / Scan, etc.). */
const INVENTORY_RESPONSIVE_MAX_WIDTH_PX = 900;

const inventoryResponsiveMediaQuery = `(max-width: ${INVENTORY_RESPONSIVE_MAX_WIDTH_PX}px)`;

interface InventoryLine {
  id: string;
  sku: string;
  referencePiece: string;
  referenceFournisseur: string;
  fournisseur: string;
  article: string;
  emplacement: string;
  quantiteTheorique: number;
  quantiteComptee: string;
  paHt: number;
  pvTtc: number;
  commentaire: string;
  /** Après « Valider », le comptage de la ligne est figé (mode inventoriste). */
  comptageValide: boolean;
  /** Après « Contrôlé », la pièce est validée par le superviseur. */
  controleValide: boolean;
  /** Email de l’inventoriste ayant validé le comptage. */
  comptePar: string;
  /** Email du superviseur ayant validé le contrôle. */
  controlePar: string;
}

interface InventoryHeaderData {
  reference: string;
  nom: string;
  site: string;
  stock: string;
  superviseur: string;
  inventoriste: string;
  statut: 'Nouveau' | 'En cours' | 'Clôturé';
}

const $Page = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
`;

const $TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const $InventoryTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
`;

const $InventoryNomTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
  color: #1e293b;
`;

const $InventoryReferenceHint = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
`;

const $TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

/** Export / import : visibles en vue large uniquement (aligné avec la grille inventaire). */
const $TopBarWideOnlyActions = styled.div`
  display: none;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  @media (min-width: 900px) {
    display: flex;
  }
`;

const $CloseInventoryModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0 1.25rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #475569;
`;

const $CloseInventoryModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const $SummaryCard = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e7e9f8;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
`;

const $SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 0.65rem 1rem;
  align-items: start;

  @media (min-width: 720px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const $SummaryCell = styled.div`
  min-width: 0;
`;

const $MetaLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const $MetaValue = styled.div`
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  word-break: break-word;
`;

const $SummaryMetaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

/** Inventoriste, vue étroite (≤900px) : switch saisie manuelle / auto sur une ligne. */
const $InventoristeResponsiveBlock = styled.div`
  display: none;
  margin-top: 1.25rem;

  @media (max-width: 900px) {
    display: block;
  }
`;

const $InventoristeEntryModeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  min-width: 0;
`;

const $EntryModeSideLabel = styled.button<{ $isActive: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: none;
  padding: 0.2rem 0;
  font-size: 0.75rem;
  line-height: 1.25;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  color: ${({ $isActive }) => ($isActive ? '#1e3a8a' : '#64748b')};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:first-of-type {
    text-align: right;
  }

  &:last-of-type {
    text-align: left;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const $EntryModeSwitch = styled.button<{ $isScan: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 2.75rem;
  height: 1.5rem;
  border-radius: 999px;
  border: 1px solid ${({ $isScan }) => ($isScan ? '#5369eb' : '#cbd5e1')};
  background: ${({ $isScan }) => ($isScan ? '#5369eb' : '#e2e8f0')};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, border-color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $isScan }) => ($isScan ? 'calc(100% - 1.25rem - 2px)' : '2px')};
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
    transition: left 0.15s ease;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $EntryModeScanAction = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0.2rem 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #5369eb;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const $LineSearchToolbar = styled.div`
  margin-top: 1.25rem;
  width: 100%;
  box-sizing: border-box;
`;

const $LineSearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  max-width: 100%;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
`;

const $LineSearchControls = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;

  @media (min-width: 640px) {
    flex: 1;
  }
`;

const $ToolbarIconButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0 0.65rem;
  border: 1px solid #ccd2f9;
  border-radius: 8px;
  background: #f5f7ff;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    border-color: #5a72ff;
    background: #eef1ff;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $AddPiecePanel = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e7e9f8;
  border-radius: 8px;
  background: #fafbff;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-width: 100%;
  box-sizing: border-box;

  @media (min-width: 720px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.65rem 0.75rem;
  }
`;

const $AddPieceField = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: 720px) {
    min-width: 160px;
    flex: 1 1 160px;
  }
`;

const $AddPieceLabel = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #334155;
`;

const $AddPieceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const $ScanBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.55);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const $ScanPanel = styled.div`
  width: min(100%, 420px);
  max-width: 420px;
  margin: auto;
  flex-shrink: 0;
  border-radius: 12px;
  background: #fff;
  padding: 1rem 1rem 0.85rem;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
`;

const $ScanTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const $ScanVideo = styled.video`
  width: 100%;
  max-height: min(52vh, 360px);
  border-radius: 8px;
  background: #0f172a;
  object-fit: cover;
`;

const $ScanMessage = styled.div<{ $variant?: 'error' | 'info' }>`
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ $variant }) => ($variant === 'error' ? '#b91c1c' : '#475569')};
`;

const $ScanActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
`;

const $LineSearchInput = styled.input`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccd2f9;
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #5a72ff;
    box-shadow: 0 0 0 1px rgba(90, 114, 255, 0.2);
  }
`;

const $EmptyFilter = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
`;

const $List = styled.div`
  margin-top: 1rem;
  display: grid;
  gap: 0.75rem;
  min-width: 0;
  max-width: 100%;
`;

const $Card = styled.div<{ $isCounted?: boolean }>`
  border: 1px solid
    ${({ theme, $isCounted }) =>
      $isCounted
        ? (theme.newUI?.variationColors?.light4 ?? '#e5e7eb')
        : '#e5e7eb'};
  border-radius: 10px;
  padding: 0.9rem;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  background: ${({ theme, $isCounted }) =>
    $isCounted
      ? (theme.newUI?.variationColors?.light5 ?? theme.colors?.lightGrey ?? '#f5f5f5')
      : (theme.systemColors?.defaultBackground ?? theme.colors?.white ?? '#fff')};
`;

const $LineWorkflowToolbar = styled.div<{ $responsiveColumnCount?: number }>`
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.65rem;

  ${({ $responsiveColumnCount }) =>
    $responsiveColumnCount
      ? `
    @media ${inventoryResponsiveMediaQuery} {
      display: grid;
      grid-template-columns: repeat(${$responsiveColumnCount}, minmax(0, 1fr));
      gap: 0.4rem;
      width: 100%;
    }
  `
      : ''}
`;

const $WorkflowFilterChip = styled.button<{ $isActive: boolean; $compactOnResponsive?: boolean }>`
  border: 1px solid ${({ $isActive }) => ($isActive ? '#5369eb' : '#ccd2f9')};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 13px;
  line-height: 1.35;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
  cursor: pointer;
  color: ${({ $isActive }) => ($isActive ? '#1e3a8a' : '#374151')};
  background: ${({ $isActive }) => ($isActive ? '#eef1ff' : '#fff')};

  ${({ $compactOnResponsive }) =>
    $compactOnResponsive
      ? `
    @media ${inventoryResponsiveMediaQuery} {
      width: 100%;
      min-width: 0;
      padding: 0.35rem 0.35rem;
      font-size: 11px;
      line-height: 1.25;
      text-align: center;
    }
  `
      : ''}

  &:hover {
    border-color: #5a72ff;
    background: #f5f7ff;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const $CardHeadMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const $CardHeadStatus = styled.div`
  flex-shrink: 0;

  @media ${inventoryResponsiveMediaQuery} {
    display: none;
  }
`;

const $CardHeadTags = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const $SuperviseurLineRow = styled.div`
  margin-top: 0.7rem;
  display: grid;
  grid-template-columns:
    minmax(5.25rem, 1.1fr)
    minmax(2.75rem, 0.55fr)
    minmax(8.5rem, 1.2fr)
    minmax(2.5rem, 0.5fr)
    minmax(5.5rem, 0.7fr)
    minmax(5.5rem, 0.7fr);
  gap: 0.5rem 1.25rem;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  & > * {
    min-width: 0;
  }

  @media (min-width: 901px) {
    align-items: start;
  }

  @media ${inventoryResponsiveMediaQuery} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem 1.25rem;

    /* Ligne 1 : PA HT / PV TTC | Qté théorique */
    & > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }

    & > *:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }

    /* Ligne 2 : Qté comptée | Écart */
    & > *:nth-child(3) {
      grid-column: 1;
      grid-row: 2;
    }

    & > *:nth-child(4) {
      grid-column: 2;
      grid-row: 2;
    }

    /* Ligne 3 : Comptée | Contrôlée */
    & > *:nth-child(5) {
      grid-column: 1;
      grid-row: 3;
    }

    & > *:nth-child(6) {
      grid-column: 2;
      grid-row: 3;
    }
  }

  @media (max-width: 1100px) and (min-width: 901px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    & > *:nth-child(3) {
      grid-column: 1 / -1;
    }
  }
`;

const $SuperviseurLineCell = styled.div<{ $minWidth?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: ${({ $minWidth }) => $minWidth ?? '0'};
  max-width: 100%;
  height: 100%;

  @media ${inventoryResponsiveMediaQuery} {
    min-width: 0;
  }
`;

const $SuperviseurLineCellBody = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 44px;
  min-width: 0;
  width: 100%;
  flex: 1;

  @media (min-width: 901px) {
    flex: 0 0 auto;
    min-height: 44px;
  }
`;

const INVENTORY_FIELD_FONT_SIZE = '14px';

const $PrixStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  line-height: 1.35;
  color: #111827;
`;

const $SuperviseurValue = styled.div`
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  line-height: 1.35;
  color: #111827;
  width: 100%;
`;

type SuperviseurEcartTone = 'negative' | 'zero' | 'positive';

const $SuperviseurEcartValue = styled.div<{ $tone?: SuperviseurEcartTone }>`
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  line-height: 1.35;
  font-weight: 600;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  width: 100%;
  color: ${({ $tone }) => {
    if ($tone === 'negative') {
      return '#b91c1c';
    }

    if ($tone === 'positive') {
      return '#1d4ed8';
    }

    if ($tone === 'zero') {
      return '#15803c';
    }

    return '#64748b';
  }};
`;

const $SuperviseurToggleCell = styled($SuperviseurLineCell)`
  min-width: 0;

  @media (min-width: 901px) {
    min-width: 5.5rem;
    padding-inline: 0.2rem;
  }

  &:nth-child(6) {
    @media (min-width: 901px) {
      padding-left: 0.65rem;
    }
  }

  @media ${inventoryResponsiveMediaQuery} {
    width: 100%;
    padding-inline: 0;

    &:nth-child(6) {
      padding-left: 0;
    }
  }
`;

const $CardTitle = styled.div`
  font-weight: 600;
  color: #111827;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const $CardSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const $InventoristeLineGrid = styled.div`
  margin-top: 0.7rem;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  align-items: start;
`;

const $InventoristeQtyCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  width: 100%;
  min-width: 0;
`;

const $InventoristeQtyActionsRow = styled.div<{ $solo?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 0.65rem;

  @media ${inventoryResponsiveMediaQuery} {
    flex-wrap: nowrap;
    align-items: center;
    gap: 0.5rem;
  }

  ${({ $solo }) =>
    $solo
      ? `
    justify-content: flex-start;
  `
      : `
    @media (min-width: 640px) {
      gap: 0.75rem;
    }
  `}
`;

const $InventoristeFieldBody = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  min-width: 0;
  width: 100%;
`;

const $InventoristeValidateWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media (min-width: 901px) {
    margin-left: auto;
    justify-content: flex-end;
  }
`;

const $QtyStepper = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 0.35rem;
  flex-shrink: 0;
  max-width: 100%;

  @media ${inventoryResponsiveMediaQuery} {
    flex-wrap: wrap;
    width: 100%;
  }
`;

/** Stepper quantité en vue superviseur responsive (demi-colonne avec l’écart). */
const $SuperviseurLineQtyStepper = styled($QtyStepper)`
  @media ${inventoryResponsiveMediaQuery} {
    flex-wrap: nowrap;
    width: 100%;
    max-width: 100%;
  }
`;

/** Stepper quantité en vue inventoriste responsive : reste sur une ligne avec « Valider ». */
const $InventoristeQtyStepper = styled($QtyStepper)`
  @media ${inventoryResponsiveMediaQuery} {
    flex-wrap: nowrap;
    width: auto;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
  }
`;

const $QtyStepButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #5369eb;
  background: #5369eb;
  font-size: 1.25rem;
  line-height: 1;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    border-color: #4458d4;
    background: #4458d4;
  }

  &:active:not(:disabled) {
    border-color: #3d4fc4;
    background: #3d4fc4;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #5a72ff;
    outline-offset: 2px;
  }
`;

const $QtyCountInputWrap = styled.div<{ $inStepper?: boolean }>`
  flex: ${({ $inStepper }) => ($inStepper ? '0 1 3.25rem' : '1')};
  min-width: 2.75rem;
  max-width: ${({ $inStepper }) => ($inStepper ? '3.5rem' : '5.5rem')};
  display: flex;
  align-items: center;

  input {
    width: 100%;
    height: 44px;
    min-height: 44px;
    box-sizing: border-box;
    font-size: ${INVENTORY_FIELD_FONT_SIZE};
    line-height: 1.35;
    text-align: center;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const $QtyLockedValue = styled.div`
  min-height: 44px;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: ${INVENTORY_FIELD_FONT_SIZE};
  color: #111827;
`;

const $FieldLabel = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: #6b7280;
  margin-bottom: 4px;
  flex-shrink: 0;
  min-height: calc(12px * 1.35);

  @media (min-width: 901px) {
    height: calc(12px * 1.35);
    margin-bottom: 4px;
    display: flex;
    align-items: flex-end;
  }
`;

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);

const getInventoryHeader = (reference: string): InventoryHeaderData => {
  const boxMartinHeader = getBoxMartinNeuillyInventoryHeader(reference);

  if (boxMartinHeader) {
    return boxMartinHeader;
  }

  switch (reference) {
    case 'INV-2026-002':
      return {
        reference,
        nom: 'Inventaire Box Jérome — Aubagne',
        site: 'Centre de réparation Aubagne',
        stock: 'Box de Jérome',
        superviseur: 'Thomas Leroy',
        inventoriste: 'Julie Martin',
        statut: 'Clôturé',
      };
    case 'INV-2026-003':
      return {
        reference,
        nom: 'Inventaire camion Jamel',
        site: 'Camion Jamel',
        stock: 'Camion Jamel',
        superviseur: 'Sophie Bernard',
        inventoriste: 'Jamel Messaoudi',
        statut: 'En cours',
      };
    case 'INV-2026-004':
      return {
        reference,
        nom: 'Inventaire camion José',
        site: 'Camion José',
        stock: 'Camion José',
        superviseur: 'Marc Fontaine',
        inventoriste: 'José Alvarez',
        statut: 'Clôturé',
      };
    case 'INV-2026-005':
      return {
        reference,
        nom: 'Inventaire Box Lucie — Argenteuil',
        site: 'Auchan Argenteuil',
        stock: 'Box de Lucie',
        superviseur: 'Élise Garnier',
        inventoriste: 'Camille Robert',
        statut: 'Nouveau',
      };
    case 'INV-2026-006':
      return {
        reference,
        nom: 'Inventaire Box Martin — prêt à clôturer',
        site: 'Auchan Neuilly sur marne',
        stock: 'Box de Martin',
        superviseur: 'Claire Dubois',
        inventoriste: 'Nicolas Petit',
        statut: 'En cours',
      };
    default:
      return {
        reference,
        nom: 'Inventaire Box Martin — Neuilly',
        site: 'Auchan Neuilly sur marne',
        stock: 'Box de Martin',
        superviseur: 'Claire Dubois',
        inventoriste: 'Nicolas Petit',
        statut: 'En cours',
      };
  }
};

const MOCK_FOURNISSEURS = [
  'Eurosav',
  'Eurosav',
  'SAV Parts Pro',
  'Samsung France',
  'LG Service',
  'Philips SAV',
  'APC Distributeur',
  'HP France',
] as const;

const getAutocompleteOptionValue = (option: unknown): string => {
  if (!option || typeof option !== 'object' || !('value' in option)) {
    return '';
  }

  const value = (option as { value?: unknown }).value;

  return typeof value === 'string' ? value : '';
};

const getSelectedCatalogPieceOption = (catalogPieceId: string) =>
  SPARE_PARTS_CATALOG_OPTIONS.find((option) => option.value === catalogPieceId);

const MOCK_REFERENCE_PIECES = [
  'NIN-HLL27196',
  'NIN-FMS127857796',
  'NIN-FMS131099915',
  'SAM-GH96-128GB',
  'LG-EDP555-001',
  'PHI-XR200-BAT',
  'APC-BK500-EU',
  'HP-FDM451-INK',
] as const;

const createMockLines = (reference: string): InventoryLine[] =>
  Array.from({ length: 8 }).map((_, index) => ({
    id: `${reference}-L${index + 1}`,
    sku: `SKU-${index + 1001}`,
    referencePiece: MOCK_REFERENCE_PIECES[index] ?? `REF-${index + 9000}`,
    referenceFournisseur: MOCK_REFERENCE_PIECES[index] ?? '',
    fournisseur: MOCK_FOURNISSEURS[index] ?? 'Eurosav',
    article: `Article ${index + 1} - Pièce SAV`,
    emplacement: index < 2 ? '-' : `A${index + 1}-B1`,
    quantiteTheorique: 5 + index * 2,
    quantiteComptee: '',
    paHt: 35 + index * 12.5,
    pvTtc: 59 + index * 21.2,
    commentaire: '',
    comptageValide: false,
    controleValide: false,
    comptePar: '',
    controlePar: '',
  }));

const applyLineCountedQuantityPersist = (
  referenceInventaire: string,
  line: InventoryLine,
  nextComptee: string,
  currentUserEmail: string,
): void => {
  if (isManualInventoryLineId(line.id)) {
    updateManualLineFields(referenceInventaire, line.id, { quantiteComptee: nextComptee });
  } else if (line.comptageValide) {
    persistMockLineComptageValide(
      referenceInventaire,
      line.id,
      nextComptee,
      line.comptePar || currentUserEmail,
    );
  }
};

const updateLineCountedQuantityInState = (
  prev: InventoryLine[],
  lineId: string,
  nextComptee: string,
  asSuperviseur: boolean,
  referenceInventaire: string,
  currentUserEmail: string,
): InventoryLine[] => {
  const item = prev.find((line) => line.id === lineId);

  if (!item) {
    return prev;
  }

  if (isInventoryClosed(referenceInventaire)) {
    return prev;
  }

  if (asSuperviseur) {
    if (item.controleValide) {
      return prev;
    }
  } else if (item.comptageValide) {
    return prev;
  }

  applyLineCountedQuantityPersist(referenceInventaire, item, nextComptee, currentUserEmail);

  return prev.map((line) =>
    line.id === lineId ? { ...line, quantiteComptee: nextComptee } : line,
  );
};

const canSupervisorMarkControle = (line: InventoryLine): boolean =>
  !line.controleValide && (line.comptageValide || line.quantiteComptee.trim() !== '');

type InventoryLineWorkflowFilter = 'all' | 'toCount' | 'toControl' | 'done';

const isLineToCount = (line: InventoryLine): boolean => !line.comptageValide;

const isLineToControl = (line: InventoryLine): boolean =>
  line.comptageValide && !line.controleValide;

const isLineDoneInMode = (line: InventoryLine, inventoryViewMode: InventoryViewMode): boolean =>
  inventoryViewMode === 'superviseur' ? line.controleValide : line.comptageValide;

const getDefaultWorkflowFilter = (inventoryViewMode: InventoryViewMode): InventoryLineWorkflowFilter =>
  inventoryViewMode === 'superviseur' ? 'toControl' : 'toCount';

const getWorkflowFilterLabel = (
  filter: InventoryLineWorkflowFilter,
  inventoryViewMode: InventoryViewMode,
): string => {
  if (filter === 'toCount') {
    return 'À compter';
  }

  if (filter === 'toControl') {
    return 'À contrôler';
  }

  if (filter === 'done') {
    return inventoryViewMode === 'superviseur' ? 'Contrôlées' : 'Comptées';
  }

  return 'Toutes';
};

const sortLinesForWorkflowAll = (
  items: InventoryLine[],
  inventoryViewMode: InventoryViewMode,
): InventoryLine[] =>
  [...items].sort((lineA, lineB) => {
    if (inventoryViewMode === 'superviseur') {
      const rank = (line: InventoryLine): number => {
        if (!line.comptageValide) {
          return 0;
        }

        if (!line.controleValide) {
          return 1;
        }

        return 2;
      };

      return rank(lineA) - rank(lineB);
    }

    if (isLineToCount(lineA) === isLineToCount(lineB)) {
      return 0;
    }

    return isLineToCount(lineA) ? -1 : 1;
  });

const normalizeCountedQuantityInput = (rawValue: string): string | null => {
  const trimmed = rawValue.trim();

  if (trimmed === '') {
    return '';
  }

  const parsed = parseInt(trimmed, 10);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return String(parsed);
};

const resolveInventoryHeader = (referenceInventaire: string): InventoryHeaderData => {
  const base = getInventoryHeader(referenceInventaire);
  const withNom = {
    ...base,
    nom: resolveInventoryNom(referenceInventaire, base.nom),
  };

  if (isInventoryClosed(referenceInventaire)) {
    return { ...withNom, statut: 'Clôturé' };
  }

  return withNom;
};

const canCloseInventorySheet = (items: InventoryLine[]): boolean =>
  items.length > 0 && items.every((line) => line.comptageValide && line.controleValide);

const mergeInventoryLinesForReference = (referenceInventaire: string): InventoryLine[] => {
  const mockValidations = loadMockLineValidationsForInventory(referenceInventaire);
  const supervisorControls = loadSupervisorControlsForInventory(referenceInventaire);

  return [
    ...createMockLines(referenceInventaire).map((line) => {
      const validation = mockValidations[line.id];

      const control = supervisorControls[line.id];

      return {
        ...line,
        comptageValide: validation !== undefined,
        quantiteComptee: validation !== undefined ? validation.quantiteComptee : line.quantiteComptee,
        comptePar: validation?.comptePar ?? '',
        controleValide: control !== undefined,
        controlePar: control?.controlePar ?? '',
      };
    }),
    ...loadManualLinesForInventory(referenceInventaire).map((line) => {
      const control = supervisorControls[line.id];

      return {
        ...line,
        comptageValide: line.comptageValide === true,
        comptePar: line.comptePar ?? '',
        controleValide: line.controleValide === true || control !== undefined,
        controlePar: line.controlePar || control?.controlePar || '',
      };
    }),
  ];
};

const getLineCountedQuantity = (quantiteComptee: string): number => {
  const trimmed = quantiteComptee.trim();

  if (trimmed === '') {
    return 0;
  }

  const n = parseInt(trimmed, 10);

  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const getLineQuantityEcart = (line: InventoryLine): number | undefined => {
  if (line.quantiteComptee.trim() === '') {
    return undefined;
  }

  return getLineCountedQuantity(line.quantiteComptee) - line.quantiteTheorique;
};

const getSuperviseurEcartTone = (ecart: number): SuperviseurEcartTone => {
  if (ecart < 0) {
    return 'negative';
  }

  if (ecart > 0) {
    return 'positive';
  }

  return 'zero';
};

const formatSuperviseurEcartDisplay = (ecart: number): string => {
  if (ecart > 0) {
    return `+${ecart}`;
  }

  return String(ecart);
};

const SuperviseurLineEcart = ({ line }: { line: InventoryLine }) => {
  const ecart = getLineQuantityEcart(line);

  if (ecart === undefined) {
    return <$SuperviseurEcartValue>—</$SuperviseurEcartValue>;
  }

  return (
    <$SuperviseurEcartValue $tone={getSuperviseurEcartTone(ecart)}>
      {formatSuperviseurEcartDisplay(ecart)}
    </$SuperviseurEcartValue>
  );
};

const hasKnownEmplacement = (emplacement: string): boolean => {
  const t = emplacement.trim();

  return t !== '' && t !== '-';
};

type BarcodeDetectorInstance = {
  detect: (image: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>;
};

type BarcodeDetectorConstructor = new (options?: { formats?: string[] }) => BarcodeDetectorInstance;

const getBarcodeDetectorConstructor = (): BarcodeDetectorConstructor | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
};

const lineMatchesSearch = (line: InventoryLine, query: string): boolean => {
  const q = query.trim().toLowerCase();

  if (!q) {
    return true;
  }

  return (
    line.sku.toLowerCase().includes(q) ||
    line.referencePiece.toLowerCase().includes(q) ||
    line.fournisseur.toLowerCase().includes(q) ||
    line.article.toLowerCase().includes(q) ||
    line.emplacement.toLowerCase().includes(q) ||
    line.id.toLowerCase().includes(q) ||
    line.referenceFournisseur.toLowerCase().includes(q)
  );
};

const findInventoryLineByScannedCode = (
  items: InventoryLine[],
  rawCode: string,
): InventoryLine | undefined => {
  const code = rawCode.trim().toLowerCase();

  if (!code) {
    return undefined;
  }

  return items.find((line) => {
    const candidates = [
      line.sku,
      line.referencePiece,
      line.referenceFournisseur,
      line.id,
    ].map((value) => value.trim().toLowerCase());

    return candidates.some((value) => value !== '' && value === code);
  });
};

const SCAN_READING_DEDUPE_MS = 1200;

const InventoryDetailPage = () => {
  const navigate = useNavigate();
  const { reference = 'INV-2026-001' } = useParams();
  const [searchParams] = useSearchParams();
  const { isThumbZoneOverlayVisible, setThumbZoneOverlayVisible } = useInventoryThumbZone();
  const currentUserEmail = useCurrentUserEmail();
  const { viewMode, setViewMode } = useInventoryViewMode();
  const [lines, setLines] = useState<InventoryLine[]>(() => mergeInventoryLinesForReference(reference));
  const [lineSearchQuery, setLineSearchQuery] = useState('');
  const [lineWorkflowFilter, setLineWorkflowFilter] = useState<InventoryLineWorkflowFilter>(() =>
    getDefaultWorkflowFilter('superviseur'),
  );
  const [inventoryClosureRevision, setInventoryClosureRevision] = useState(0);
  const [inventoryNomRevision, setInventoryNomRevision] = useState(0);
  const [isMobileQrModalOpen, setIsMobileQrModalOpen] = useState(false);
  const [isCloseInventoryModalOpen, setIsCloseInventoryModalOpen] = useState(false);
  const [isScanCameraOpen, setIsScanCameraOpen] = useState(false);
  const [isAddPieceOpen, setIsAddPieceOpen] = useState(false);
  const [addPieceCatalogPieceId, setAddPieceCatalogPieceId] = useState('');
  const [scanCameraError, setScanCameraError] = useState('');
  const [scanCameraInfo, setScanCameraInfo] = useState('');
  const scanVideoRef = useRef<HTMLVideoElement | null>(null);
  const scanRafRef = useRef<number | null>(null);
  const scanBackdropRef = useRef<HTMLDivElement | null>(null);
  const inventoryImportFileInputRef = useRef<HTMLInputElement | null>(null);
  const inventoryScanBehaviorRef = useRef({
    mode: 'superviseur' as InventoryViewMode,
    inventoristeLayoutEntryMode: 'manuel' as InventoristeResponsiveEntryMode,
    isInventoryResponsiveLayout: false,
  });
  const lastScanDedupeRef = useRef<{ value: string; time: number } | null>(null);

  const [isInventoryResponsiveLayout, setIsInventoryResponsiveLayout] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${INVENTORY_RESPONSIVE_MAX_WIDTH_PX}px)`).matches
      : false,
  );
  const [inventoristeLayoutEntryMode, setInventoristeLayoutEntryMode] =
    useState<InventoristeResponsiveEntryMode>('manuel');
  const onScanCodeDetectedRef = useRef<(rawCode: string) => void>(() => {});

  useEffect(() => {
    setLineSearchQuery('');
    setIsScanCameraOpen(false);
    setIsAddPieceOpen(false);
    setAddPieceCatalogPieceId('');
    setThumbZoneOverlayVisible(false);
    setInventoristeLayoutEntryMode('manuel');
    lastScanDedupeRef.current = null;
  }, [reference, setThumbZoneOverlayVisible]);

  useEffect(() => {
    return () => {
      setThumbZoneOverlayVisible(false);
    };
  }, [setThumbZoneOverlayVisible]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mq = window.matchMedia(`(max-width: ${INVENTORY_RESPONSIVE_MAX_WIDTH_PX}px)`);
    const onChange = () => {
      setIsInventoryResponsiveLayout(mq.matches);
    };

    onChange();
    mq.addEventListener('change', onChange);

    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isInventoryResponsiveLayout) {
      setInventoristeLayoutEntryMode('manuel');
      lastScanDedupeRef.current = null;
      setIsScanCameraOpen(false);
    }
  }, [isInventoryResponsiveLayout]);

  useLayoutEffect(() => {
    if (!isScanCameraOpen) {
      return undefined;
    }

    const vv = window.visualViewport;

    if (!vv) {
      return undefined;
    }

    const el = scanBackdropRef.current;

    if (!el) {
      return undefined;
    }

    const applyVisualViewport = () => {
      el.style.setProperty('height', `${vv.height}px`);
      el.style.setProperty('width', `${vv.width}px`);
      el.style.setProperty('top', `${vv.offsetTop}px`);
      el.style.setProperty('left', `${vv.offsetLeft}px`);
    };

    applyVisualViewport();
    vv.addEventListener('resize', applyVisualViewport);
    vv.addEventListener('scroll', applyVisualViewport);

    return () => {
      vv.removeEventListener('resize', applyVisualViewport);
      vv.removeEventListener('scroll', applyVisualViewport);
      el.style.removeProperty('height');
      el.style.removeProperty('width');
      el.style.removeProperty('top');
      el.style.removeProperty('left');
    };
  }, [isScanCameraOpen]);

  useEffect(() => {
    setLines(mergeInventoryLinesForReference(reference));
    setLineWorkflowFilter(getDefaultWorkflowFilter(viewMode));
  }, [viewMode, reference]);

  const header = useMemo(() => {
    void inventoryClosureRevision;
    void inventoryNomRevision;

    return resolveInventoryHeader(reference);
  }, [reference, inventoryClosureRevision, inventoryNomRevision]);

  const isInventorySheetClosed = useMemo(() => {
    void inventoryClosureRevision;

    return isInventoryClosed(reference);
  }, [reference, inventoryClosureRevision]);

  const canValidateInventorySheet = useMemo(
    () => canCloseInventorySheet(lines) && !isInventorySheetClosed,
    [isInventorySheetClosed, lines],
  );

  useEffect(() => {
    const onClosureChanged = () => {
      setInventoryClosureRevision((revision) => revision + 1);
    };

    const onNomChanged = () => {
      setInventoryNomRevision((revision) => revision + 1);
    };

    window.addEventListener(POC_INVENTORY_CLOSURE_EVENT, onClosureChanged);
    window.addEventListener(POC_INVENTORY_NOM_EVENT, onNomChanged);

    return () => {
      window.removeEventListener(POC_INVENTORY_CLOSURE_EVENT, onClosureChanged);
      window.removeEventListener(POC_INVENTORY_NOM_EVENT, onNomChanged);
    };
  }, []);

  const inventoryMobileOpenUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return buildInventoryMobileOpenUrl('', reference, viewMode);
    }

    return buildInventoryMobileOpenUrl(window.location.origin, reference, viewMode);
  }, [reference, viewMode]);

  useEffect(() => {
    const viewModeFromQr = parseInventoryViewModeFromMobileQrSearch(searchParams);

    if (viewModeFromQr !== undefined) {
      setViewMode(viewModeFromQr);
    }
  }, [searchParams, setViewMode]);

  const toCountLineCount = useMemo(
    () => lines.filter((line) => isLineToCount(line)).length,
    [lines],
  );

  const toControlLineCount = useMemo(
    () => lines.filter((line) => isLineToControl(line)).length,
    [lines],
  );

  const filteredLines = useMemo(() => {
    const searchMatches = lines.filter((line) => lineMatchesSearch(line, lineSearchQuery));

    if (lineWorkflowFilter === 'toCount') {
      return searchMatches.filter((line) => isLineToCount(line));
    }

    if (lineWorkflowFilter === 'toControl') {
      return searchMatches.filter((line) => isLineToControl(line));
    }

    if (lineWorkflowFilter === 'done') {
      return searchMatches.filter((line) => isLineDoneInMode(line, viewMode));
    }

    return sortLinesForWorkflowAll(searchMatches, viewMode);
  }, [lineSearchQuery, lineWorkflowFilter, lines, viewMode]);

  const counted = lines.filter((line) => line.comptageValide).length;
  const controlledCount = lines.filter((line) => line.controleValide).length;
  const totalPaHt = lines.reduce((acc, line) => acc + line.paHt, 0);
  const totalPvTtc = lines.reduce((acc, line) => acc + line.pvTtc, 0);

  const handleCloseScanCamera = useCallback(() => {
    setIsScanCameraOpen(false);
  }, []);

  const handleSetInventoristeEntryManuel = useCallback(() => {
    setInventoristeLayoutEntryMode('manuel');
    lastScanDedupeRef.current = null;
    setIsScanCameraOpen(false);
  }, []);

  const handleSetInventoristeEntryScan = useCallback(() => {
    setInventoristeLayoutEntryMode('scan');
    setLineSearchQuery('');
    setIsAddPieceOpen(false);
    setScanCameraError('');
    setScanCameraInfo('');
    lastScanDedupeRef.current = null;
    setIsScanCameraOpen(true);
  }, []);

  const handleOpenCloseInventoryModal = useCallback(() => {
    if (!canValidateInventorySheet) {
      return;
    }

    setIsCloseInventoryModalOpen(true);
  }, [canValidateInventorySheet]);

  const handleCloseCloseInventoryModal = useCallback(() => {
    setIsCloseInventoryModalOpen(false);
  }, []);

  const handleConfirmCloseInventory = useCallback(() => {
    if (!canCloseInventorySheet(lines)) {
      toast.warn('Toutes les pièces doivent être comptées et contrôlées avant de clôturer l’inventaire.', {
        autoClose: 4000,
        showIcon: true,
      });

      return;
    }

    if (isInventoryClosed(reference)) {
      return;
    }

    const quantiteTheorique = lines.reduce((sum, line) => sum + line.quantiteTheorique, 0);
    const quantiteComptee = lines.reduce(
      (sum, line) => sum + getLineCountedQuantity(line.quantiteComptee),
      0,
    );
    const sommePaHt = lines.reduce((sum, line) => sum + line.paHt, 0);
    const sommePvTtc = lines.reduce((sum, line) => sum + line.pvTtc, 0);
    const { ecartPaHt, ecartPvTtc } = computeInventoryAmountEcartsFromLines(
      lines.map((line) => ({
        quantiteTheorique: line.quantiteTheorique,
        quantiteComptee: getLineCountedQuantity(line.quantiteComptee),
        paHt: line.paHt,
        pvTtc: line.pvTtc,
      })),
    );

    const closure = persistInventoryClosure({
      reference,
      quantiteTheorique,
      quantiteComptee,
      sommePaHt,
      sommePvTtc,
      ecartPaHt,
      ecartPvTtc,
      nombreArticles: lines.length,
      closedBy: currentUserEmail,
    });

    const imputation = applyInventoryStockImputation(
      reference,
      lines.map((line) => ({
        referencePiece: line.referencePiece,
        quantiteTheorique: line.quantiteTheorique,
        quantiteComptee: getLineCountedQuantity(line.quantiteComptee),
      })),
      closure,
    );

    setInventoryClosureRevision((revision) => revision + 1);
    setIsCloseInventoryModalOpen(false);

    const ecartLabel =
      imputation.totalEcart > 0
        ? `+${imputation.totalEcart}`
        : String(imputation.totalEcart);

    toast.success(
      imputation.linesWithEcart > 0
        ? `Inventaire clôturé. Stock imputé : ${imputation.linesWithEcart} référence(s), ` +
            `écart total ${ecartLabel} unité(s). Un reçu fiscal sera généré.`
        : 'Inventaire clôturé. Aucun écart à imputer au stock. Un reçu fiscal sera généré.',
      { autoClose: 5000, showIcon: true },
    );

    navigate('/inventaire');
  }, [currentUserEmail, lines, navigate, reference]);

  const handleExportInventory = useCallback(() => {
    toast.info(
      `Export (POC) — inventaire ${reference} : à brancher sur l’API ou le téléchargement de fichier.`,
      { autoClose: 4000, showIcon: true },
    );
  }, [reference]);

  const handleClickImportInventory = useCallback(() => {
    inventoryImportFileInputRef.current?.click();
  }, []);

  const handleInventoryImportFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) {
      return;
    }

    toast.info(`Import (POC) — fichier « ${file.name} » : traitement à brancher.`, {
      autoClose: 4500,
      showIcon: true,
    });
  }, []);

  const handleSetLineCountedQuantity = useCallback(
    (lineId: string, rawValue: string, asSuperviseur = false) => {
      const nextComptee = normalizeCountedQuantityInput(rawValue);

      if (nextComptee === null) {
        return;
      }

      setLines((prev) =>
        updateLineCountedQuantityInState(
          prev,
          lineId,
          nextComptee,
          asSuperviseur,
          reference,
          currentUserEmail,
        ),
      );
    },
    [currentUserEmail, reference],
  );

  const handleAdjustLineCount = useCallback(
    (lineId: string, delta: number, asSuperviseur = false) => {
      setLines((prev) => {
        const item = prev.find((line) => line.id === lineId);

        if (!item) {
          return prev;
        }

        const nextComptee = String(
          Math.max(0, getLineCountedQuantity(item.quantiteComptee) + delta),
        );

        return updateLineCountedQuantityInState(
          prev,
          lineId,
          nextComptee,
          asSuperviseur,
          reference,
          currentUserEmail,
        );
      });
    },
    [currentUserEmail, reference],
  );

  const handleAddManualPiece = useCallback(() => {
    const catalogEntry = getSparePartCatalogEntry(addPieceCatalogPieceId);

    if (!catalogEntry) {
      return;
    }

    const id = `${reference}-manual-${Date.now()}`;
    const defaultPaHt = 25;
    const defaultPvTtc = 45;
    const newLine: InventoryLine = {
      id,
      sku: catalogEntry.referencePieceDetachee,
      referencePiece: catalogEntry.referenceConstructeur,
      referenceFournisseur: catalogEntry.referenceFournisseur,
      fournisseur: catalogEntry.fournisseur,
      article: catalogEntry.nomPiece,
      emplacement: '-',
      quantiteTheorique: 1,
      quantiteComptee: '',
      paHt: defaultPaHt,
      pvTtc: defaultPvTtc,
      commentaire: '',
      comptageValide: false,
      controleValide: false,
      comptePar: '',
      controlePar: '',
    };

    appendManualLineToInventory(reference, {
      ...newLine,
      comptageValide: false,
      controleValide: false,
      comptePar: '',
      controlePar: '',
    });
    setLines((prev) => [...prev, newLine]);
    setAddPieceCatalogPieceId('');
    setLineSearchQuery('');
    toast.success(`Pièce « ${catalogEntry.nomPiece} » ajoutée à l’inventaire.`, {
      autoClose: 3000,
      showIcon: true,
    });
  }, [addPieceCatalogPieceId, reference]);

  const handleValiderComptage = useCallback(
    (lineId: string) => {
      setLines((prev) => {
        const item = prev.find((line) => line.id === lineId);

        if (!item || item.comptageValide) {
          return prev;
        }

        const qtyAtValidate = String(getLineCountedQuantity(item.quantiteComptee));

        if (isManualInventoryLineId(lineId)) {
          updateManualLineFields(reference, lineId, {
            comptageValide: true,
            quantiteComptee: qtyAtValidate,
            comptePar: currentUserEmail,
          });
        } else {
          persistMockLineComptageValide(reference, lineId, qtyAtValidate, currentUserEmail);
        }

        return prev.map((line) =>
          line.id === lineId
            ? {
                ...line,
                comptageValide: true,
                quantiteComptee: qtyAtValidate,
                comptePar: currentUserEmail,
              }
            : line,
        );
      });
    },
    [currentUserEmail, reference],
  );

  const handleValiderControleSuperviseur = useCallback(
    (lineId: string) => {
      setLines((prev) => {
        const item = prev.find((line) => line.id === lineId);

        if (!item || item.controleValide || !canSupervisorMarkControle(item)) {
          return prev;
        }

        const qtyAtValidate = String(getLineCountedQuantity(item.quantiteComptee));
        const withComptage = item.comptageValide
          ? item
          : {
              ...item,
              comptageValide: true,
              quantiteComptee: qtyAtValidate,
              comptePar: currentUserEmail,
            };

        if (!item.comptageValide) {
          if (isManualInventoryLineId(lineId)) {
            updateManualLineFields(reference, lineId, {
              comptageValide: true,
              quantiteComptee: qtyAtValidate,
              comptePar: currentUserEmail,
            });
          } else {
            persistMockLineComptageValide(reference, lineId, qtyAtValidate, currentUserEmail);
          }
        }

        if (isManualInventoryLineId(lineId)) {
          updateManualLineFields(reference, lineId, {
            controleValide: true,
            controlePar: currentUserEmail,
          });
        } else {
          persistSupervisorControlValide(reference, lineId, currentUserEmail);
        }

        return prev.map((line) =>
          line.id === lineId
            ? {
                ...withComptage,
                controleValide: true,
                controlePar: currentUserEmail,
              }
            : line,
        );
      });
    },
    [currentUserEmail, reference],
  );

  useEffect(() => {
    if (!isScanCameraOpen) {
      return undefined;
    }

    let isCancelled = false;
    const streamHolder: { current: MediaStream | null } = { current: null };

    const cancelFrame = () => {
      if (scanRafRef.current !== null) {
        cancelAnimationFrame(scanRafRef.current);
        scanRafRef.current = null;
      }
    };

    const releaseCamera = () => {
      cancelFrame();
      if (streamHolder.current) {
        streamHolder.current.getTracks().forEach((track) => track.stop());
        streamHolder.current = null;
      }
      const video = scanVideoRef.current;

      if (video) {
        video.srcObject = null;
      }
    };

    const scheduleDetect = (detector: BarcodeDetectorInstance) => {
      const step = () => {
        if (isCancelled) {
          return;
        }

        const video = scanVideoRef.current;

        if (!video) {
          scanRafRef.current = requestAnimationFrame(step);

          return;
        }

        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          detector
            .detect(video)
            .then((codes) => {
              if (isCancelled) {
                return;
              }

              const raw = codes[0]?.rawValue;

              if (raw) {
                const behavior = inventoryScanBehaviorRef.current;
                const isScanSessionListMode =
                  behavior.isInventoryResponsiveLayout &&
                  behavior.inventoristeLayoutEntryMode === 'scan';

                if (isScanSessionListMode) {
                  const now = Date.now();
                  const last = lastScanDedupeRef.current;

                  if (last && last.value === raw && now - last.time < SCAN_READING_DEDUPE_MS) {
                    scanRafRef.current = requestAnimationFrame(step);

                    return;
                  }

                  lastScanDedupeRef.current = { value: raw, time: now };
                  onScanCodeDetectedRef.current(raw);
                  scanRafRef.current = requestAnimationFrame(step);

                  return;
                }

                lastScanDedupeRef.current = null;
                onScanCodeDetectedRef.current(raw);

                return;
              }

              scanRafRef.current = requestAnimationFrame(step);
            })
            .catch(() => {
              if (!isCancelled) {
                scanRafRef.current = requestAnimationFrame(step);
              }
            });
        } else {
          scanRafRef.current = requestAnimationFrame(step);
        }
      };

      scanRafRef.current = requestAnimationFrame(step);
    };

    const run = async () => {
      setScanCameraError('');
      setScanCameraInfo('');

      if (!navigator.mediaDevices?.getUserMedia) {
        setScanCameraError("La caméra n'est pas disponible dans ce navigateur.");

        return;
      }

      let stream: MediaStream;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch {
        setScanCameraError(
          "Impossible d'activer la caméra. Vérifiez les permissions ou utilisez une connexion sécurisée (HTTPS).",
        );

        return;
      }

      if (isCancelled) {
        stream.getTracks().forEach((track) => track.stop());

        return;
      }

      streamHolder.current = stream;
      const video = scanVideoRef.current;

      if (video) {
        video.setAttribute('playsinline', 'true');
        video.playsInline = true;
        video.muted = true;
        video.srcObject = stream;
        try {
          await video.play();
        } catch {
          // lecture vidéo peut échouer selon le navigateur
        }
      }

      if (isCancelled) {
        releaseCamera();

        return;
      }

      const BarcodeDetectorCtor = getBarcodeDetectorConstructor();

      if (!BarcodeDetectorCtor) {
        setScanCameraInfo(
          'La lecture automatique du code-barres nécessite un navigateur compatible (ex. Chrome sur Android). ' +
            'La caméra est active : cadrez le code ou saisissez-le à la main dans le champ recherche.',
        );

        return;
      }

      try {
        const detector = new BarcodeDetectorCtor({
          formats: [
            'aztec',
            'code_128',
            'code_39',
            'code_93',
            'codabar',
            'data_matrix',
            'ean_13',
            'ean_8',
            'itf',
            'pdf417',
            'qr_code',
            'upc_a',
            'upc_e',
          ],
        });

        scheduleDetect(detector);
      } catch {
        setScanCameraInfo(
          "Impossible d'initialiser la détection des codes-barres. La caméra reste ouverte pour aide visuelle.",
        );
      }
    };

    void run();

    return () => {
      isCancelled = true;
      releaseCamera();
    };
  }, [isScanCameraOpen]);

  useEffect(() => {
    if (!isScanCameraOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseScanCamera();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isScanCameraOpen, handleCloseScanCamera]);

  inventoryScanBehaviorRef.current = {
    mode: viewMode,
    inventoristeLayoutEntryMode,
    isInventoryResponsiveLayout,
  };

  onScanCodeDetectedRef.current = (rawCode: string) => {
    const code = rawCode.trim();

    if (!code) {
      return;
    }

    const behavior = inventoryScanBehaviorRef.current;
    const isResponsiveScanIncrement =
      behavior.mode === 'inventoriste' &&
      behavior.inventoristeLayoutEntryMode === 'scan' &&
      behavior.isInventoryResponsiveLayout;

    const isResponsiveScanSearch =
      behavior.mode === 'superviseur' &&
      behavior.inventoristeLayoutEntryMode === 'scan' &&
      behavior.isInventoryResponsiveLayout;

    if (isResponsiveScanIncrement) {
      setLines((prev) => {
        const line = findInventoryLineByScannedCode(prev, code);

        if (!line) {
          toast.warn(`Aucune pièce trouvée pour « ${code} ».`, {
            autoClose: 3500,
            showIcon: true,
          });

          return prev;
        }

        if (line.comptageValide) {
          toast.info(`« ${line.referencePiece} » est déjà validée.`, {
            autoClose: 3500,
            showIcon: true,
          });

          return prev;
        }

        const nextComptee = String(getLineCountedQuantity(line.quantiteComptee) + 1);

        toast.success(`+1 · ${line.referencePiece} (qté ${nextComptee})`, {
          autoClose: 2200,
          showIcon: true,
        });

        return updateLineCountedQuantityInState(
          prev,
          line.id,
          nextComptee,
          false,
          reference,
          currentUserEmail,
        );
      });

      return;
    }

    if (isResponsiveScanSearch) {
      setLineSearchQuery(code);

      return;
    }

    setLineSearchQuery(code);
    setIsScanCameraOpen(false);
  };

  const showResponsiveEntryModeToggle = isInventoryResponsiveLayout;

  const showLineSearchInput =
    !isInventoryResponsiveLayout || inventoristeLayoutEntryMode === 'manuel';

  const isResponsiveScanIncrementActive =
    viewMode === 'inventoriste' &&
    isInventoryResponsiveLayout &&
    inventoristeLayoutEntryMode === 'scan';

  const isResponsiveScanSearchActive =
    viewMode === 'superviseur' &&
    isInventoryResponsiveLayout &&
    inventoristeLayoutEntryMode === 'scan';

  return (
    <$Page>
      <Panel>
        <$TopBar>
          <$InventoryTitleBlock>
            <$InventoryNomTitle>{header.nom}</$InventoryNomTitle>
            <$InventoryReferenceHint>{header.reference}</$InventoryReferenceHint>
          </$InventoryTitleBlock>
          <$TopBarRight>
            <$TopBarWideOnlyActions>
              {viewMode !== 'inventoriste' ? (
                <input
                  ref={inventoryImportFileInputRef}
                  type="file"
                  accept={
                    '.csv,.xlsx,.xls,text/csv,' +
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                  }
                  style={{ display: 'none' }}
                  aria-hidden
                  tabIndex={-1}
                  onChange={handleInventoryImportFileChange}
                />
              ) : null}
              <$ToolbarIconButton
                type="button"
                onClick={() => setIsMobileQrModalOpen(true)}
                aria-label="Afficher le QR code pour ouvrir sur mobile"
                title="Ouvrir sur mobile (QR code)"
              >
                <QrCodeIcon size="22px" color="#334155" />
              </$ToolbarIconButton>
              {viewMode !== 'inventoriste' ? (
                <>
                  <Button type={BUTTON_TYPES.BUTTON} secondary onClick={handleExportInventory}>
                    Exporter
                  </Button>
                  <Button type={BUTTON_TYPES.BUTTON} secondary onClick={handleClickImportInventory}>
                    Importer
                  </Button>
                </>
              ) : null}
            </$TopBarWideOnlyActions>
            {viewMode === 'superviseur' && !isInventorySheetClosed && !isInventoryResponsiveLayout ? (
              <Button
                type={BUTTON_TYPES.BUTTON}
                primary
                onClick={handleOpenCloseInventoryModal}
                disabled={!canValidateInventorySheet}
                title={
                  canValidateInventorySheet
                    ? 'Clôturer l’inventaire et imputer les écarts au stock'
                    : 'Comptez et contrôlez toutes les pièces avant de clôturer'
                }
              >
                Clôturer l'inventaire
              </Button>
            ) : null}
            <$ToolbarIconButton
              type="button"
              onClick={() => navigate('/inventaire')}
              aria-label="Fermer la fiche inventaire"
              title="Fermer"
            >
              <CrossIcon size="22px" color="#334155" />
            </$ToolbarIconButton>
          </$TopBarRight>
        </$TopBar>

        <$SummaryCard>
          <$SummaryGrid>
            <$SummaryCell>
              <$MetaLabel>Stock</$MetaLabel>
              <$SummaryMetaStack>
                <$MetaValue>{header.stock}</$MetaValue>
                <$MetaValue>{header.site}</$MetaValue>
              </$SummaryMetaStack>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Statut</$MetaLabel>
              <div style={{ marginTop: 2 }}>
                <Tag
                  alert={header.statut === 'En cours'}
                  success={header.statut === 'Clôturé'}
                  primary={header.statut === 'Nouveau'}
                  withoutMargin
                  isSmall
                >
                  {header.statut}
                </Tag>
              </div>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Superviseur</$MetaLabel>
              <$MetaValue>{header.superviseur}</$MetaValue>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Inventoriste</$MetaLabel>
              <$MetaValue>{header.inventoriste}</$MetaValue>
            </$SummaryCell>
            <$SummaryCell>
              <$MetaLabel>Progression</$MetaLabel>
              {viewMode === 'superviseur' ? (
                <$SummaryMetaStack>
                  <$MetaValue>
                    {counted} / {lines.length} pièces comptées
                  </$MetaValue>
                  <$MetaValue>
                    {controlledCount} / {lines.length} pièces contrôlées
                  </$MetaValue>
                </$SummaryMetaStack>
              ) : (
                <$MetaValue>
                  {counted} / {lines.length} articles comptés
                </$MetaValue>
              )}
            </$SummaryCell>
            {viewMode === 'superviseur' ? (
              <$SummaryCell>
                <$MetaLabel>Totaux théoriques</$MetaLabel>
                <$SummaryMetaStack>
                  <$MetaValue>PA HT {formatAmount(totalPaHt)}</$MetaValue>
                  <$MetaValue>PV TTC {formatAmount(totalPvTtc)}</$MetaValue>
                </$SummaryMetaStack>
              </$SummaryCell>
            ) : null}
          </$SummaryGrid>
        </$SummaryCard>

        {showResponsiveEntryModeToggle ? (
          <$InventoristeResponsiveBlock>
            <$InventoristeEntryModeRow>
              <$EntryModeSideLabel
                type="button"
                $isActive={inventoristeLayoutEntryMode === 'manuel'}
                onClick={handleSetInventoristeEntryManuel}
                title="Rechercher une pièce et l’ajouter"
              >
                Saisie manuelle
              </$EntryModeSideLabel>
              <$EntryModeSwitch
                type="button"
                role="switch"
                $isScan={inventoristeLayoutEntryMode === 'scan'}
                aria-checked={inventoristeLayoutEntryMode === 'scan'}
                aria-label={
                  inventoristeLayoutEntryMode === 'scan'
                    ? 'Passer en saisie manuelle'
                    : 'Passer en saisie automatique (scan)'
                }
                onClick={() => {
                  if (inventoristeLayoutEntryMode === 'scan') {
                    handleSetInventoristeEntryManuel();
                  } else {
                    handleSetInventoristeEntryScan();
                  }
                }}
              />
              <$EntryModeSideLabel
                type="button"
                $isActive={inventoristeLayoutEntryMode === 'scan'}
                onClick={handleSetInventoristeEntryScan}
                title={
                  viewMode === 'superviseur'
                    ? 'Scanner : chaque code filtre la liste des pièces'
                    : 'Scanner : chaque code ajoute +1 sur la pièce'
                }
              >
                Saisie auto
              </$EntryModeSideLabel>
              {inventoristeLayoutEntryMode === 'scan' && !isScanCameraOpen ? (
                <$EntryModeScanAction
                  type="button"
                  onClick={handleSetInventoristeEntryScan}
                >
                  Ouvrir
                </$EntryModeScanAction>
              ) : null}
            </$InventoristeEntryModeRow>
          </$InventoristeResponsiveBlock>
        ) : null}

        <$LineSearchToolbar>
          {showLineSearchInput ? (
            <>
              <$LineSearchRow>
                <$LineSearchControls>
                  <$LineSearchInput
                    id="inventory-line-search"
                    type="search"
                    name="inventory-line-search"
                    value={lineSearchQuery}
                    onChange={(event) => setLineSearchQuery(event.target.value)}
                    placeholder="Rechercher une pièce"
                    aria-label="Rechercher une pièce"
                    autoComplete="off"
                    spellCheck={false}
                    enterKeyHint="search"
                    inputMode="search"
                  />
                  <Button
                    type={BUTTON_TYPES.BUTTON}
                    secondary
                    onClick={() => setIsAddPieceOpen((open) => !open)}
                    aria-expanded={isAddPieceOpen}
                    aria-label={
                      isAddPieceOpen
                        ? 'Fermer le formulaire d’ajout de pièce'
                        : 'Ouvrir le formulaire pour ajouter une pièce à l’inventaire'
                    }
                    title={isAddPieceOpen ? 'Fermer l’ajout de pièce' : 'Ajouter une pièce'}
                  >
                    Ajouter
                  </Button>
                </$LineSearchControls>
              </$LineSearchRow>
              {isAddPieceOpen ? (
                <$AddPiecePanel>
                  <$AddPieceField>
                    <$AddPieceLabel htmlFor="add-piece-catalog">Pièce du catalogue</$AddPieceLabel>
                    <Autocomplete
                      name="add-piece-catalog"
                      options={SPARE_PARTS_CATALOG_OPTIONS}
                      value={getSelectedCatalogPieceOption(addPieceCatalogPieceId)}
                      placeholder="Rechercher une pièce détachée…"
                      onChange={(option) => {
                        setAddPieceCatalogPieceId(getAutocompleteOptionValue(option));
                      }}
                    />
                  </$AddPieceField>
                  <$AddPieceActions>
                    <Button
                      type={BUTTON_TYPES.BUTTON}
                      secondary
                      onClick={handleAddManualPiece}
                      disabled={!addPieceCatalogPieceId.trim()}
                    >
                      Ajouter
                    </Button>
                  </$AddPieceActions>
                </$AddPiecePanel>
              ) : null}
            </>
          ) : null}
          <$LineWorkflowToolbar
            $responsiveColumnCount={
              showResponsiveEntryModeToggle ? (viewMode === 'superviseur' ? 3 : 2) : undefined
            }
          >
            <$WorkflowFilterChip
              type="button"
              $isActive={lineWorkflowFilter === 'toCount'}
              $compactOnResponsive={showResponsiveEntryModeToggle}
              onClick={() => setLineWorkflowFilter('toCount')}
            >
              À compter ({toCountLineCount})
            </$WorkflowFilterChip>
            {viewMode === 'superviseur' ? (
              <$WorkflowFilterChip
                type="button"
                $isActive={lineWorkflowFilter === 'toControl'}
                $compactOnResponsive={showResponsiveEntryModeToggle}
                onClick={() => setLineWorkflowFilter('toControl')}
              >
                À contrôler ({toControlLineCount})
              </$WorkflowFilterChip>
            ) : null}
            <$WorkflowFilterChip
              type="button"
              $isActive={lineWorkflowFilter === 'all'}
              $compactOnResponsive={showResponsiveEntryModeToggle}
              onClick={() => setLineWorkflowFilter('all')}
            >
              Toutes ({lines.length})
            </$WorkflowFilterChip>
          </$LineWorkflowToolbar>
        </$LineSearchToolbar>

        <$List>
          {filteredLines.length === 0 ? (
            <$EmptyFilter>
              {lineSearchQuery.trim() !== ''
                ? `Aucune pièce ne correspond à « ${lineSearchQuery.trim()} » avec le filtre « ${getWorkflowFilterLabel(
                    lineWorkflowFilter,
                    viewMode,
                  )} ».`
                : lineWorkflowFilter === 'toCount'
                  ? 'Aucune pièce restante à compter.'
                  : lineWorkflowFilter === 'toControl'
                    ? 'Aucune pièce restante à contrôler (comptage validé requis).'
                    : lineWorkflowFilter === 'done'
                      ? 'Aucune pièce terminée pour ce filtre.'
                      : 'Aucune pièce dans cet inventaire.'}
            </$EmptyFilter>
          ) : (
            filteredLines.map((line) => (
              <$Card key={line.id} $isCounted={line.comptageValide}>
                <$CardHead>
                  <$CardHeadMain>
                    <$CardTitle>
                      {line.referencePiece} - {line.fournisseur}
                    </$CardTitle>
                    {hasKnownEmplacement(line.emplacement) ? (
                      <$CardSub>{line.emplacement}</$CardSub>
                    ) : null}
                  </$CardHeadMain>
                  <$CardHeadStatus>
                    <$CardHeadTags>
                      {line.controleValide ? (
                        <Tag primary withoutMargin isSmall>
                          Contrôlé
                        </Tag>
                      ) : line.comptageValide ? (
                        <Tag success withoutMargin isSmall>
                          Compté
                        </Tag>
                      ) : (
                        <Tag alert withoutMargin isSmall>
                          A compter
                        </Tag>
                      )}
                    </$CardHeadTags>
                  </$CardHeadStatus>
                </$CardHead>

                {viewMode === 'superviseur' ? (
                  <$SuperviseurLineRow>
                    <$SuperviseurLineCell $minWidth="5.25rem">
                      <$FieldLabel>PA HT / PV TTC</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <$PrixStack>
                          <span>{formatAmount(line.paHt)}</span>
                          <span>{formatAmount(line.pvTtc)}</span>
                        </$PrixStack>
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="2.5rem">
                      <$FieldLabel>Qté théorique</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <$SuperviseurValue>{line.quantiteTheorique}</$SuperviseurValue>
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="8.5rem">
                      <$FieldLabel>Qté comptée</$FieldLabel>
                      <$SuperviseurLineCellBody>
                      {line.controleValide ? (
                        <$SuperviseurValue>{line.quantiteComptee || '—'}</$SuperviseurValue>
                      ) : (
                        <$SuperviseurLineQtyStepper>
                          <$QtyStepButton
                            type="button"
                            aria-label="Diminuer la quantité comptée"
                            disabled={getLineCountedQuantity(line.quantiteComptee) <= 0}
                            onClick={() => handleAdjustLineCount(line.id, -1, true)}
                          >
                            -
                          </$QtyStepButton>
                          <$QtyCountInputWrap $inStepper>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              name={`superviseur-qty-${line.id}`}
                              value={line.quantiteComptee}
                              onChange={(event) => {
                                handleSetLineCountedQuantity(line.id, event.target.value, true);
                              }}
                              aria-label="Quantité comptée"
                            />
                          </$QtyCountInputWrap>
                          <$QtyStepButton
                            type="button"
                            aria-label="Augmenter la quantité comptée"
                            onClick={() => handleAdjustLineCount(line.id, 1, true)}
                          >
                            +
                          </$QtyStepButton>
                        </$SuperviseurLineQtyStepper>
                      )}
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurLineCell $minWidth="2rem">
                      <$FieldLabel>Écart</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <SuperviseurLineEcart line={line} />
                      </$SuperviseurLineCellBody>
                    </$SuperviseurLineCell>
                    <$SuperviseurToggleCell>
                      <$FieldLabel>Comptée</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <Switch
                          name={`superviseur-comptee-${line.id}`}
                          checked={line.comptageValide}
                          isDisabled={line.comptageValide || line.controleValide}
                          isSmall
                          dataTestId={`inventory-line-comptee-${line.id}`}
                          onChange={(event) => {
                            if (event.target.checked) {
                              handleValiderComptage(line.id);
                            }
                          }}
                        />
                      </$SuperviseurLineCellBody>
                    </$SuperviseurToggleCell>
                    <$SuperviseurToggleCell>
                      <$FieldLabel>Contrôlée</$FieldLabel>
                      <$SuperviseurLineCellBody>
                        <Switch
                          name={`superviseur-controlee-${line.id}`}
                          checked={line.controleValide}
                          isDisabled={!canSupervisorMarkControle(line)}
                          isSmall
                          dataTestId={`inventory-line-controlee-${line.id}`}
                          onChange={(event) => {
                            if (event.target.checked) {
                              handleValiderControleSuperviseur(line.id);
                            }
                          }}
                        />
                      </$SuperviseurLineCellBody>
                    </$SuperviseurToggleCell>
                  </$SuperviseurLineRow>
                ) : (
                  <$InventoristeLineGrid>
                    <$InventoristeQtyCell>
                      <$FieldLabel>Quantité comptée</$FieldLabel>
                      <$InventoristeFieldBody>
                      {!line.comptageValide ? (
                        <$InventoristeQtyActionsRow>
                          <$InventoristeQtyStepper>
                            <$QtyStepButton
                              type="button"
                              aria-label="Diminuer la quantité comptée"
                              disabled={getLineCountedQuantity(line.quantiteComptee) <= 0}
                              onClick={() => handleAdjustLineCount(line.id, -1)}
                            >
                              -
                            </$QtyStepButton>
                            <$QtyCountInputWrap $inStepper>
                              <Input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                name={`inventoriste-qty-${line.id}`}
                                value={line.quantiteComptee}
                                onChange={(event) => {
                                  handleSetLineCountedQuantity(line.id, event.target.value);
                                }}
                                aria-label="Quantité comptée"
                              />
                            </$QtyCountInputWrap>
                            <$QtyStepButton
                              type="button"
                              aria-label="Augmenter la quantité comptée"
                              onClick={() => handleAdjustLineCount(line.id, 1)}
                            >
                              +
                            </$QtyStepButton>
                          </$InventoristeQtyStepper>
                          <$InventoristeValidateWrap>
                            <Button
                              type={BUTTON_TYPES.BUTTON}
                              primary
                              onClick={() => handleValiderComptage(line.id)}
                            >
                              Valider
                            </Button>
                          </$InventoristeValidateWrap>
                        </$InventoristeQtyActionsRow>
                      ) : (
                        <$InventoristeQtyActionsRow $solo>
                          <$QtyLockedValue aria-live="polite">
                            {line.quantiteComptee || '0'}
                          </$QtyLockedValue>
                        </$InventoristeQtyActionsRow>
                      )}
                      </$InventoristeFieldBody>
                    </$InventoristeQtyCell>
                  </$InventoristeLineGrid>
                )}
              </$Card>
            ))
          )}
        </$List>
      </Panel>
      <ThumbZoneOverlay visible={isThumbZoneOverlayVisible} />
      <Modal
        isOpen={isCloseInventoryModalOpen}
        size={MODAL_SIZES.SMALL}
        onClose={handleCloseCloseInventoryModal}
        dataTestId="inventory-close-confirm-modal"
      >
        <Heading level={3}>Clôturer l&apos;inventaire</Heading>
        <$CloseInventoryModalBody>
          <p style={{ margin: 0 }}>
            Vous êtes sur le point de clôturer l&apos;inventaire <strong>{header.nom}</strong>.
          </p>
          <p style={{ margin: 0 }}>
            Cette action est définitive&nbsp;: aucun retour en arrière ne sera possible.
          </p>
          <p style={{ margin: 0 }}>
            À la validation de la clôture, les stocks seront imputés automatiquement des écarts
            constatés.
          </p>
          <p style={{ margin: 0 }}>Un reçu fiscal sera alors généré.</p>
        </$CloseInventoryModalBody>
        <$CloseInventoryModalFooter>
          <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleCloseCloseInventoryModal}>
            Annuler
          </Button>
          <Button
            type={BUTTON_TYPES.BUTTON}
            primary
            onClick={handleConfirmCloseInventory}
            dataTestId="inventory-close-confirm"
          >
            Valider la clôture
          </Button>
        </$CloseInventoryModalFooter>
      </Modal>
      <InventoryMobileQrModal
        isOpen={isMobileQrModalOpen}
        mobileUrl={inventoryMobileOpenUrl}
        onClose={() => setIsMobileQrModalOpen(false)}
      />
      {typeof document !== 'undefined' &&
        isScanCameraOpen &&
        createPortal(
          <$ScanBackdrop ref={scanBackdropRef} onClick={handleCloseScanCamera}>
            <$ScanPanel onClick={(event) => event.stopPropagation()}>
              <$ScanTitle id="inventory-scan-title">
                {isResponsiveScanIncrementActive
                  ? 'Scan inventaire — +1 par code'
                  : isResponsiveScanSearchActive
                    ? 'Scan inventaire — recherche par code'
                    : 'Scanner un code-barres'}
              </$ScanTitle>
              {scanCameraError ? <$ScanMessage $variant="error">{scanCameraError}</$ScanMessage> : null}
              {scanCameraInfo ? <$ScanMessage $variant="info">{scanCameraInfo}</$ScanMessage> : null}
              <$ScanVideo ref={scanVideoRef} autoPlay muted playsInline />
              <$ScanActions>
                <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleCloseScanCamera}>
                  Fermer
                </Button>
              </$ScanActions>
            </$ScanPanel>
          </$ScanBackdrop>,
          document.body,
        )}
    </$Page>
  );
};

export default InventoryDetailPage;
